/**
 * Shared file validation utility for email attachments.
 * Single source of truth for allowed file types, size limits, and validation logic
 * used across QuickReplyModal, MassMailModal, AttachmentManager, etc.
 */

export const ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'image/heic', 'image/heic-sequence',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text
  'text/plain', 'text/csv',
  // Video
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm',
  // Archives
  'application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed',
];

/** Regex for extension-based fallback (used when MIME is empty or octet-stream) */
export const ALLOWED_EXTENSIONS_REGEX =
  /\.(jpg|jpeg|png|gif|webp|svg|heic|heif|pdf|doc|docx|xls|xlsx|xlsm|xlsb|ppt|pptx|txt|csv|mp4|mov|avi|mkv|webm|zip|rar|7z)$/i;

/** 25 MB per file */
export const MAX_FILE_SIZE = 25 * 1024 * 1024;

/** 100 MB total across all attachments */
export const MAX_TOTAL_SIZE = 100 * 1024 * 1024;

/** Value for the HTML file input `accept` attribute */
export const FILE_INPUT_ACCEPT =
  '.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb,.ppt,.pptx,.txt,.csv,.mp4,.mov,.avi,.mkv,.webm,.zip,.rar,.7z';

/**
 * Check whether a file's MIME type or extension is in the allow-list.
 * Handles the common browser issue where files may be sent as
 * `application/octet-stream` (e.g. .xlsx, .heic on some OSes).
 */
export const isFileTypeAllowed = (file: File): boolean => {
  if (ALLOWED_MIME_TYPES.includes(file.type)) return true;
  // Fallback: check file extension for octet-stream or empty MIME
  if (!file.type || file.type === 'application/octet-stream') {
    return ALLOWED_EXTENSIONS_REGEX.test(file.name);
  }
  // Final fallback: just check the extension regardless of MIME
  return ALLOWED_EXTENSIONS_REGEX.test(file.name);
};

/**
 * Validate a single file for upload. Returns an error string or null if valid.
 */
export const validateFileForUpload = (
  file: File,
  currentTotalSize: number = 0,
): string | null => {
  if (!isFileTypeAllowed(file)) {
    return `Bestandstype niet toegestaan: ${file.name}`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `Bestand te groot (max 25MB): ${file.name}`;
  }
  if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
    return `Totale bestandsgrootte overschrijdt 100MB limiet`;
  }
  return null;
};

/** Human-readable file size */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
